import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from "@angular/core";
import { UtilsService } from "@core/services/utils/utils.service";
import { environment } from "@env/environment";
import { TranslateService } from "@ngx-translate/core";

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: "astrobin-turnstile-captcha",
  templateUrl: "./turnstile-captcha.component.html",
  styleUrls: ["./turnstile-captcha.component.scss"]
})
export class TurnstileCaptchaComponent implements OnInit, OnDestroy {
  @ViewChild("turnstileContainer", { static: true }) turnstileContainer: ElementRef;
  @Input() mode: "managed" | "invisible" = "managed"; // Default to visible widget
  @Output() tokenReceived = new EventEmitter<string>();
  @Output() tokenExpired = new EventEmitter<void>();
  @Output() error = new EventEmitter<string>();

  @HostBinding("class.invisible-mode") get isInvisible(): boolean {
    return this.mode === "invisible";
  }

  private widgetId: string | undefined;
  private readonly siteKey = environment.turnstileSiteKey;
  private renderRetryCount = 0;
  private executeRetryCount = 0;
  private readonly maxRetries = 20; // 2 seconds with 100ms intervals
  private readonly errorMessage: string;

  constructor(
    private readonly ngZone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly translate: TranslateService,
    private readonly utilsService: UtilsService
  ) {
    this.errorMessage = this.translate.instant(
      "Security verification is being blocked by your browser or network. " +
        "Please disable ad blockers, privacy extensions, or try a different browser. " +
        "If the issue persists, contact support at support@astrobin.com"
    );
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadTurnstileScript();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.widgetId !== undefined && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  }

  private loadTurnstileScript(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (window.turnstile) {
      this.renderWidget();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.renderWidget();
    };
    document.head.appendChild(script);
  }

  private renderWidget(): void {
    if (!isPlatformBrowser(this.platformId) || !window.turnstile || !this.turnstileContainer) {
      if (isPlatformBrowser(this.platformId)) {
        this.renderRetryCount++;
        if (this.renderRetryCount >= this.maxRetries) {
          this.ngZone.run(() => {
            this.error.emit(this.errorMessage);
          });
          return;
        }
        this.utilsService.delay(100).subscribe(() => this.renderWidget());
      }
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const config: any = {
        sitekey: this.siteKey,
        callback: (token: string) => {
          this.ngZone.run(() => {
            this.tokenReceived.emit(token);
          });
        },
        "expired-callback": () => {
          this.ngZone.run(() => {
            this.tokenExpired.emit();
          });
        },
        theme: "auto"
      };

      if (this.mode === "invisible") {
        // For invisible mode, use interaction-only which doesn't show a widget
        config.appearance = "interaction-only";
        config.size = "flexible"; // Use flexible size for invisible mode
      } else {
        // For visible mode
        config.appearance = "always";
        config.size = "normal";
      }

      this.widgetId = window.turnstile.render(this.turnstileContainer.nativeElement, config);
    });
  }

  reset(): void {
    if (isPlatformBrowser(this.platformId) && this.widgetId !== undefined && window.turnstile) {
      window.turnstile.reset(this.widgetId);
    }
  }

  execute(): void {
    if (isPlatformBrowser(this.platformId) && this.widgetId !== undefined && window.turnstile) {
      this.executeRetryCount = 0; // Reset counter on successful execute
      window.turnstile.execute(this.widgetId);
    } else if (isPlatformBrowser(this.platformId) && !this.widgetId) {
      // Widget not ready yet, wait and retry
      this.executeRetryCount++;
      if (this.executeRetryCount >= this.maxRetries) {
        this.ngZone.run(() => {
          this.error.emit(this.errorMessage);
        });
        return;
      }
      this.utilsService.delay(100).subscribe(() => this.execute());
    }
  }
}
